import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Field, Form, getFormValues, reduxForm } from 'redux-form';
import {
  listPossibleLeaveParticipants,
  listPossibleWorkSites,
} from '../../redux/actions/table/timesheets';
import styles from '../../styles/forms/add_timesheet_wizard/AddTimesheetForm.scss';
import Button from '../../components/Button';
import ButtonGroup from '../../components/ButtonGroup';
import { withRouter } from 'react-router';
import { subscribe } from 'react-contextual';
import Localization from '../../helpers/Localization';
import { connect } from 'react-redux';
import { __ } from '../../core/utils';
import Select from '../../components/Select';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Checkbox from '../../components/Checkbox';
import DateTimePicker from '../../components/DateTimePicker';
import { listPossibleMeetingTasks } from '../../redux/actions/table/all_meetings';
import { listPossibleSubstagesWithChildrenByStage } from '../../redux/actions/table/all_tasks';
import {
  listPossibleSpecificationProjects,
  listPossibleSpecificationStages,
} from '../../redux/actions/table/working_set_specifications';
import classNames from 'classnames';
import { createAddressBookCompany } from '../../redux/actions/table/address_book_companies';
import SearchableDropdown from '../../components/SearchableDropdown';
import ArrowDownSmallIcon from '../../assets/images/arrow-down-small-15x15.svg';
import Tooltip from '../../components/Tooltip';

const normalizeDropdownOptions = (options = []) =>
  _.map(options, (option, index) => ({
    ...option,
    group_option_key:
      option?.group_option_key || `__group__${option?.group_id}`,
    option_key:
      option?.option_key ||
      ['be', option?.group_id, option?.value, option?.label, index].join('_'),
  }));

const AddTimesheetForm = (props) => {
  const prevPropRef = useRef();

  const [possible_projects, set_possible_projects] = useState([]);
  const [possible_stages, set_possible_stages] = useState([]);
  const [possible_meeting_tasks, set_possible_meeting_tasks] = useState([]);
  const [
    possible_substages_with_children,
    set_possible_substages_with_children,
  ] = useState([]);
  const [possible_work_sites, set_possible_work_sites] = useState([]);
  const [possible_users_for_entry, set_possible_users_for_entry] = useState([]);

  useEffect(() => {
    const active_project = props.values.project_id;
    const active_stage = props.values.stage_id;

    listPossibleSpecificationProjects().then(({ response }) => {
      const projects = response.data;

      set_possible_projects(projects);

      props.change(
        'is_billable',
        _.find(projects, (p) => p.id === props.values?.project_id).is_billable
      );
    });

    if (active_project) {
      listPossibleSpecificationStages(active_project).then(({ response }) => {
        set_possible_stages(response.data);
      });
    }

    if (active_stage) {
      listPossibleMeetingTasks(active_stage).then(({ response }) => {
        set_possible_meeting_tasks(response.data);
      });

      listPossibleSubstagesWithChildrenByStage(active_stage).then(
        ({ response }) => {
          set_possible_substages_with_children(response.data || []);
        }
      );
    }

    listPossibleWorkSites().then(({ response }) => {
      set_possible_work_sites(response.data);
    });

    listPossibleLeaveParticipants().then(({ response }) => {
      set_possible_users_for_entry([
        ..._.map(response.data, (row) => ({
          value: row.id,
          label: row.fullname,
        })),
      ]);
    });

    props.change('task_id', props.values.task_id);
  }, []);

  useEffect(() => {
    const project_id = props.values.project_id;

    if (
      prevPropRef.current !== undefined &&
      prevPropRef.current !== project_id
    ) {
      listPossibleSpecificationStages(project_id).then(({ response }) => {
        set_possible_stages(response.data);

        props.change('stage_id', null);
      });

      props.change(
        'is_billable',
        _.find(possible_projects, (p) => p.id === project_id).is_billable
      );
    }

    prevPropRef.current = project_id;
  }, [props.values]);

  useEffect(() => {
    const {
      values: { stage_id },
      anyTouch,
    } = props;

    if (stage_id) {
      listPossibleMeetingTasks(stage_id).then(({ response }) => {
        set_possible_meeting_tasks(response.data);

        anyTouch && props.change('task_id', null);
      });

      listPossibleSubstagesWithChildrenByStage(stage_id).then(
        ({ response }) => {
          set_possible_substages_with_children(response.data || []);
        }
      );
    } else {
      set_possible_meeting_tasks([]);
      set_possible_substages_with_children([]);
      props.change('task_id', null);
    }
  }, [props.values.stage_id]);

  const _handleSubmit = (edit) => {
    props.change('edit', edit);

    setTimeout(() => props.submit(), 10);
  };

  const createNewWorksite = (value) => {
    createAddressBookCompany({ name: value, is_office: 1 }).then(
      ({ response }) => {
        listPossibleWorkSites()
          .then(({ response }) => {
            set_possible_work_sites(response.data);
          })
          .then(() => {
            props.change('address_book_company_id', _.get(response.data, 'id'));
          });
      }
    );
  };

  const possibleMinutes = [
    { value: '0', label: '00' },
    { value: 15, label: '15' },
    { value: 30, label: '30' },
    { value: 45, label: '45' },
  ];

  const { values, auth } = props;

  const [selected, setSelected] = useState({
    values: [],
    type: null,
    substage_id: null,
    task_id: null,
  });

  const dropdownOptions = normalizeDropdownOptions(
    possible_substages_with_children
  );

  useEffect(() => {
    const taskId = props.values.task_id;
    const selectedTask = _.find(dropdownOptions, ['value', taskId]);

    if (selectedTask) {
      const substageId = selectedTask.group_id || null;

      setSelected((prevSelected) => ({
        ...prevSelected,
        values: [selectedTask.option_key],
        substage_id: substageId,
        task_id: taskId,
        type: 'child',
      }));
      props.change('substage_id', substageId);
    }
  }, [possible_substages_with_children]);

  const selectedTaskOption = _.find(dropdownOptions, {
    value: selected.task_id,
  });
  const selectedGroupOption = _.find(dropdownOptions, {
    group_id: selected.substage_id,
  });
  const selectedLabel =
    selected.type === 'group'
      ? _.get(selectedGroupOption, 'group')
      : _.get(selectedTaskOption, 'label');

  return (
    <Form className={styles.wrapper}>
      <div className={styles.form}>
        <div className={styles.flex}>
          {_.includes(auth?.permissions, 'create.any.timesheet.entry') && (
            <Field
              name='user_id'
              component={Select}
              searchable
              options={possible_users_for_entry}
              label={__('table.columns.timesheet-users')}
              placeholder={__('add-timesheet-form.select-user')}
            />
          )}
        </div>
        <div className={styles.flex}>
          <div className={styles.f2}>
            <Field
              name='date'
              component={DateTimePicker}
              label={__('table.columns.timesheet-date')}
              footer
            />
          </div>

          <div className={styles.f2}>
            <Field
              name='hours'
              component={Input}
              label={__('table.columns.timesheet-hours')}
              numeric
              maxLength={2}
              hint={__('timesheets.hint.in-hours')}
            />
          </div>
          <div className={styles.f2}>
            <Field
              name='minutes'
              component={Select}
              searchable
              options={possibleMinutes}
              label={__('table.columns.timesheet-minutes')}
            />
          </div>
        </div>
        <div className={styles.flex}>
          <div className={styles.f1}>
            <Field
              name='project_id'
              searchable={true}
              component={Select}
              label={__('add-timesheet-form.project')}
              options={_.map(possible_projects, (project) => ({
                value: project.id,
                label: [project.code, project.name].join(': '),
              }))}
              placeholder={__('add-timesheet-form.add-project')}
            />
          </div>
          <div className={styles.f1}>
            <Field
              name='stage_id'
              disabled={!values.project_id}
              searchable={true}
              component={Select}
              label={__('add-timesheet-form.stage')}
              options={_.map(possible_stages, (stage) => ({
                value: stage.id,
                label: [_.padStart(stage.order, 2, '0'), stage.name].join(': '),
              }))}
              placeholder={__('add-timesheet-form.add-stage')}
            />
          </div>
        </div>

        {/* <SearchableDropdown options={options} multiselect={true} /> */}
        <SearchableDropdown
          options={dropdownOptions}
          dropdownWrapperClassName={styles.dropdownWrapper}
          multiselect
          closable={false}
          top={30}
          header={
            <div className={styles.dropdownHeader}>
              <Tooltip
                text={__('add-timesheet-form.substages-and-tasks')}
                className={styles.filterHeaderName}
              >
                {__('add-timesheet-form.substages-and-tasks')}
              </Tooltip>

              <span
                className={styles.filterHeaderClearAll}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelected({
                    values: [],
                    type: null,
                    substage_id: null,
                    task_id: null,
                  });
                  props.change('task_id', null);
                  props.change('substage_id', null);
                }}
              >
                {__('add-timesheet-form.substages-and-tasks.clear')}
              </span>
            </div>
          }
          selectedValues={selected.values}
          name={'substage_task_selector'}
          leftStyled
          onSelect={(item, groupLabel) => {
            if (item.groupHeading) {
              const groupValues = _.map(
                _.filter(dropdownOptions, (option) =>
                  item.group_id
                    ? option.group_id === item.group_id
                    : option.group === groupLabel
                ),
                'option_key'
              );

              setSelected({
                values: [item.group_option_key, ...groupValues],
                type: 'group',
                substage_id: item.group_id || null,
                task_id: null,
              });
              props.change('task_id', null);
              props.change('substage_id', item.group_id || null);
              return;
            }

            setSelected({
              values: [item.option_key || item.value],
              type: 'child',
              substage_id: item.group_id || null,
              task_id: item.value,
            });
            props.change('task_id', item.value || null);
            props.change('substage_id', item.group_id || null);
          }}
          onRemove={() => {
            setSelected({
              values: [],
              type: null,
              substage_id: null,
              task_id: null,
            });
            props.change('task_id', null);
            props.change('substage_id', null);
          }}
        >
          {/* <div className={styles.label}>
            <label htmlFor={'specification_subgroup_ids'}>
              <Tooltip text={__('note.flyout.specification_subgroup.label')}>
                {__('note.flyout.specification_subgroup.label')}
              </Tooltip>
            </label>
          </div> */}

          <div
            className={classNames(
              styles.bubble
              // _.size(selectedFilterValues[filter.name]) === 0 &&
              //   styles.bubbleEmpty
            )}
          >
            <label className={styles.bubbleLabel}>
              {__('add-timesheet-form.substages-and-tasks')}
            </label>
            <span
              className={classNames(!selectedLabel && styles.bubblePlaceholder)}
            >
              {selectedLabel ||
                __('add-timesheet-form.choose-substage-or-task')}
            </span>
            {/* {_.size(selectedFilterValues[filter.name]) > 0 && (
              <strong className={styles.filterCount}>
                ({_.size(selectedFilterValues[filter.name])})
              </strong>
            )} */}
            <ArrowDownSmallIcon className={styles.bubbleArrow} />
          </div>

          {/* <div className={styles.dropdownTrigger}>
            Open dropdown ({selected.length})
          </div> */}
        </SearchableDropdown>
        <Field name='task_id' component='input' type='hidden' />
        <Field name='substage_id' component='input' type='hidden' />
        {/* <Field
          name='task_id'
          searchable={true}
          component={Select}
          label={__('table.columns.timesheet-task')}
          options={_.map(possible_meeting_tasks, (task) => ({
            value: task.id,
            label: ['T-', task.identifier, ': ', task.title],
          }))}
          placeholder={__('add-timesheet-form.select-task')}
        /> */}
        <div className={styles.flex}>
          <div className={styles.f5}>
            <Field
              name='address_book_company_id'
              searchable={true}
              component={Select}
              label={__('add-timesheet-form.work-site')}
              options={_.map(possible_work_sites, (workSite) => ({
                value: workSite.id,
                label: workSite.name,
              }))}
              callbackSetNewValue={(value) => {
                createNewWorksite(value);
              }}
              placeholder={__('add-timesheet-form.select-location')}
            />
          </div>
          <div className={styles.dropdowns}>
            <div className={styles.f4}>
              <Field
                name='is_billable'
                component={Checkbox}
                label={__('add-timesheet-form.nonbillable')}
                className={styles.checkbox}
              />
            </div>
          </div>
        </div>
        <Field
          name='description'
          component={Textarea}
          label={__('add-timesheet-form.description')}
          maxLength={200}
          rows={1}
          maxRows={5}
          placeholder={__('add-timesheet-form.enter-description')}
        />
        <div className={styles.footer}>
          <Button
            lightGray
            medium
            left
            middleText={__('button.cancel')}
            onClick={props.actions.hideAddTimesheetWizard}
          />
          <ButtonGroup>
            <Button
              lightBlue
              medium
              middleText={__('button.create')}
              spinning={props.submitting}
              onClick={() => _handleSubmit(false)}
            />
            {/* {this.props.match.path == '/stages/:stage/tasks' && (
                <Button
                  lightBlue
                  medium
                  middleText={__('button.create-edit')}
                  spinning={this.props.submitting}
                  onClick={() => this._handleSubmit(true)}
                />
              )} */}
          </ButtonGroup>
        </div>
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => {
  return {
    values: getFormValues('add_timesheet_wizard.time_entry')(state),
    auth: state.auth,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: {
      hideAddTimesheetWizard: () =>
        dispatch({
          type: 'table.timesheets/SET_TABLE_PARAMS',
          params: {
            wizard_timesheet_add: undefined,
            task_id: undefined,
          },
        }),
    },
  };
};

export default withRouter(
  reduxForm({
    form: 'add_timesheet_wizard.time_entry',
  })(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(subscribe(Localization, 'localization')(AddTimesheetForm))
  )
);
